/**
 * 登录页
 *
 * 单个表单：填了「租户标识」走租户登录，留空走平台运营登录
 * （与后端 /api/auth/login 的判断逻辑一致，前端不做额外分流）。
 *
 * 【为什么页面上没有演示账号了】那一排快捷填充把 tenantCode / 账号 / **密码**
 * 直接写在页面与打包产物里，任何打开过登录页的人都能拿到各角色的口令。
 * 演示环境才需要它，而演示环境不等于线上——把口令留在页面里，
 * 就是在赌"这套代码永远不会部署到能被外人访问的地方"。
 *
 * 【左栏的文案只陈述已经做到的事】「凭证由人生成」「高风险默认阻断」这些
 * 都是系统里真有硬约束的（见 docs/技术开发文档 第十一节），
 * 不写"AI 智能审核""秒级风控"这类没实现的话。
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { LockOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/stores/auth';

interface LoginFormValues {
  tenantCode?: string;
  username: string;
  password: string;
}

/** 左栏四条：每条都对应系统里一处真实的硬约束或能力 */
const POINTS: ReadonlyArray<{ title: string; text: string }> = [
  { title: '多维自动校验', text: 'OCR、企查查、电子税务局、合同比对，四路交叉核验' },
  { title: '只做风险标记', text: '自动校验不替你下结论，关键节点保留人工确认' },
  { title: '凭证由人生成', text: '仅同步业务数据至 T+ 待办池，会计科目由财务在 T+ 端选' },
  { title: '高风险默认阻断', text: '红冲、作废等默认拦截流转，需特例放行并留痕' },
];

export default function Login(): React.ReactElement {
  const [form] = Form.useForm<LoginFormValues>();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: LoginFormValues): Promise<void> => {
    setError(null);
    try {
      const auth = await login({
        ...(values.tenantCode ? { tenantCode: values.tenantCode.trim() } : {}),
        username: values.username.trim(),
        password: values.password,
      });
      const redirect = params.get('redirect');
      const fallback = auth.principalType === 'PLATFORM' ? '/platform/metrics' : '/dashboard';
      navigate(redirect && redirect.startsWith('/') ? redirect : fallback, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败，请稍后重试');
    }
  };

  return (
    <div className="ilc-login-bg">
      <div className="ilc-login-card">
        <div className="ilc-login-left">
          <div className="ilc-auth-brand">
            <div className="ilc-auth-seal">票</div>
            <div>
              <div className="ilc-auth-wordmark">票链云</div>
              <div className="ilc-auth-wordmark-latin">Invoice Link Cloud</div>
            </div>
          </div>

          <div>
            <p className="ilc-auth-claim">
              发票核验 · 财务记账
              <br />
              数字化平台
            </p>
          </div>

          <ul className="ilc-auth-points">
            {POINTS.map((point, i) => (
              <li key={point.title}>
                <span className="idx">{String(i + 1).padStart(2, '0')}</span>
                <span>
                  <b>{point.title}</b>
                  <br />
                  {point.text}
                </span>
              </li>
            ))}
          </ul>

          <div className="ilc-auth-left-foot">
            多租户 SaaS · 数据按租户隔离
            <br />
            外部接口调用日志全量留存，可追溯
          </div>
        </div>

        <div className="ilc-login-right">
          <div className="ilc-auth-eyebrow">账户登录</div>
          <h1 className="ilc-auth-title">欢迎回来</h1>
          <Typography.Text className="ilc-auth-sub">
            租户用户请填写租户标识；平台运营人员留空即可
          </Typography.Text>

          {error ? (
            <Alert
              type="error"
              showIcon
              message={error}
              style={{ marginTop: 16, borderRadius: 3 }}
              closable
              onClose={() => setError(null)}
            />
          ) : null}

          <Form<LoginFormValues>
            form={form}
            className="ilc-auth-form"
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => void handleSubmit(values)}
            initialValues={{ tenantCode: '', username: '', password: '' }}
          >
            {/**
             * 租户标识。
             *
             * 【placeholder 口头改成"必填"（2026-09-23 用户要求）】从前这里写的是
             * 「留空 = 平台运营登录」——对平台运营人员友好，但对**绝大多数租户用户**
             * 是个误导：他们这一栏是必填的，看到"留空"反而会犹豫。
             * 现在把"必填"放进口头，平台运营那条路挪到下面的小字里说清楚。
             *
             * 注意**不能真的设成 required**：平台运营人员就是要留空才能登进运营端
             * （见《管理员使用手册》第 1 节），设成必填会把那条路堵死。
             */}
            <Form.Item
              name="tenantCode"
              label="租户标识"
              extra="不记得标识？问贵司管理员，或联系平台客服"
            >
              <Input
                size="large"
                prefix={<ShopOutlined style={{ color: 'rgba(13,39,64,0.28)' }} />}
                placeholder="必填（平台运营人员留空）"
                autoComplete="organization"
                allowClear
              />
            </Form.Item>

            <Form.Item name="username" label="账号" rules={[{ required: true, message: '请输入账号' }]}>
              <Input
                size="large"
                prefix={<UserOutlined style={{ color: 'rgba(13,39,64,0.28)' }} />}
                placeholder="登录账号"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password
                size="large"
                prefix={<LockOutlined style={{ color: 'rgba(13,39,64,0.28)' }} />}
                placeholder="登录密码"
                autoComplete="current-password"
                onPressEnter={() => form.submit()}
              />
            </Form.Item>

            <Button
              type="primary"
              size="large"
              htmlType="submit"
              block
              loading={loading}
              className="ilc-auth-submit"
            >
              登录
            </Button>
          </Form>

          <div className="ilc-auth-register">
            还没有账号？<Link to="/register">企业自助注册</Link>
          </div>

          <div className="ilc-auth-foot">
            <span>数据按租户隔离</span>
            <span>权限变更即时生效</span>
            <span>全部写操作留痕</span>
          </div>
        </div>
      </div>
    </div>
  );
}
